import React from "react";
import { getDownloadInfo, useLatestVersion } from "../download";
import { useT } from "../i18n/context";

/**
 * Reusable building blocks for blog post bodies. These mirror the helpers used
 * in the newsletter emails (section / featureRows / bullets / cta / divider) so
 * posts read as the same surface — but rendered as React with the site's theme
 * CSS variables, so they adapt to light/dark automatically.
 *
 * Import what you need at the top of a post and compose freely. Because the body
 * is plain JSX you can also drop in any custom markup or interactive component.
 */

const BLUE = "#5ba8f5";

/** A titled section: small uppercase label above a block of content. */
export function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 36 }}>
      {title && (
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 25,
            lineHeight: 1.25,
            fontWeight: 600,
            letterSpacing: "-0.015em",
            color: "var(--text-primary)",
          }}
        >
          {title}
        </p>
      )}
      {children}
    </section>
  );
}

/** A standard body paragraph. */
export function P({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: "0 0 16px",
        fontSize: 16,
        lineHeight: 1.7,
        color: "var(--text-primary)",
      }}
    >
      {children}
    </p>
  );
}

/** A larger lead/intro paragraph, used right under the title. */
export function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: "0 0 24px",
        fontSize: 19,
        lineHeight: 1.6,
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </p>
  );
}

/** A sub-heading inside the body. */
export function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        margin: "32px 0 12px",
        fontSize: 24,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: "var(--text-primary)",
      }}
    >
      {children}
    </h2>
  );
}

/** An inline-content image with an optional caption. `src` is an imported asset. */
export function Img({ src, alt = "", caption }: { src: string; alt?: string; caption?: string }) {
  return (
    <figure style={{ margin: "28px 0" }}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          borderRadius: 10,
          border: "1px solid var(--border-subtle)",
        }}
      />
      {caption && (
        <figcaption
          style={{ marginTop: 10, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/** A bulleted list. */
export function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ margin: "0 0 16px", padding: 0, listStyle: "none" }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{
            position: "relative",
            paddingLeft: 22,
            margin: "8px 0",
            fontSize: 16,
            lineHeight: 1.65,
            color: "var(--text-primary)",
          }}
        >
          <span style={{ position: "absolute", left: 2, top: 0, color: BLUE }}>&bull;</span>
          {it}
        </li>
      ))}
    </ul>
  );
}

/** A monospaced code block. Pass raw text as children. */
export function Code({ children }: { children: React.ReactNode }) {
  return (
    <pre
      style={{
        margin: "20px 0",
        padding: "16px 18px",
        borderRadius: 10,
        overflowX: "auto",
        fontFamily: "var(--font-mono)",
        fontSize: 13.5,
        lineHeight: 1.6,
        color: "var(--text-primary)",
        backgroundColor: "var(--surface-4)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <code>{children}</code>
    </pre>
  );
}

/** An aside panel — a plain, bright card (no accent rule). */
export function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        margin: "24px 0",
        padding: "18px 22px",
        borderRadius: 12,
        border: "1px solid var(--border-strong)",
        backgroundColor: "var(--surface-5)",
        color: "var(--text-secondary)",
        fontSize: 15,
        lineHeight: 1.65,
      }}
    >
      {children}
    </div>
  );
}

/** A filled call-to-action button (external links open in a new tab). */
export function CTA({ label, href }: { label: string; href: string }) {
  const external = /^https?:/.test(href);
  return (
    <div style={{ margin: "8px 0 24px" }}>
      <a
        href={href}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        style={{
          display: "inline-block",
          padding: "11px 26px",
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 500,
          color: "#fff",
          backgroundColor: BLUE,
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

/**
 * A full-width call-to-action card: a title, an optional line of body copy, and a
 * filled button. Use to close a post with the product pitch in one contained block.
 *
 * Pass `download` to turn the button into the site's platform-aware Download
 * button (same URL/label as the homepage). Pass `bgImage` to layer a screenshot
 * behind the card with a dark scrim so the copy stays readable.
 */
export function CTACard({
  title,
  children,
  label,
  href,
  download,
  github,
  bgImage,
}: {
  title?: string;
  children?: React.ReactNode;
  label?: string;
  href?: string;
  download?: boolean;
  /** Add a secondary "Star on GitHub" button next to the primary CTA. */
  github?: boolean;
  bgImage?: string;
}) {
  const { home } = useT();
  const version = useLatestVersion();
  const dl = getDownloadInfo(version);

  const buttonHref = download ? dl.url : href ?? "/";
  const buttonLabel = label ?? (download ? home.download[dl.platform] : "Meet CATE");
  const external = /^https?:/.test(buttonHref);

  // Over a dark screenshot the theme's body text colors would be unreadable, so
  // force light copy whenever a background image is present.
  const titleColor = bgImage ? "#fff" : "var(--text-primary)";
  const bodyColor = bgImage ? "rgba(255,255,255,0.82)" : "var(--text-secondary)";

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        margin: "44px 0 32px",
        // a screenshot card holds a fixed aspect ratio, so its vertical room shrinks
        // on narrow viewports; scale the padding down with it so the buttons keep room.
        padding: bgImage
          ? "clamp(16px, 5vw, 40px) clamp(12px, 3.4vw, 28px) clamp(18px, 5.5vw, 44px)"
          : "40px 28px 44px",
        // a screenshot card echoes a native macOS window corner; the plain card stays at 20
        borderRadius: bgImage ? 12 : 20,
        textAlign: "center",
        border: `1px solid ${BLUE}3d`,
        background:
          "radial-gradient(120% 130% at 50% -10%, rgba(91,168,245,0.16), rgba(91,168,245,0.02) 60%, transparent), var(--surface-4)",
        // When the card carries a screenshot, give it the screenshot's own aspect
        // ratio so the image fills the card edge-to-edge with no cropping.
        ...(bgImage
          ? {
              // width must stay definite, else aspect-ratio resolves the *width* from
              // the content height and the card overflows narrow viewports. Pinning
              // width keeps height the derived dimension so the AR is honored.
              width: "100%",
              aspectRatio: "2000 / 1256",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }
          : {}),
      }}
    >
      {/* optional screenshot background + dark scrim for legibility */}
      {bgImage && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(180deg, rgba(8,10,14,0.58) 0%, rgba(8,10,14,0.74) 100%), url(${bgImage})`,
            // box AR matches the image AR, so cover fills it with no crop
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            pointerEvents: "none",
          }}
        />
      )}
      {/* soft accent glow behind the title */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -90,
          left: "50%",
          transform: "translateX(-50%)",
          width: 260,
          height: 180,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${BLUE}2e, transparent)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", maxWidth: 460, margin: "0 auto" }}>
        {title && (
          <p
            style={{
              margin: 0,
              fontSize: 23,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: titleColor,
            }}
          >
            {title}
          </p>
        )}
        {children && (
          <p
            style={{
              margin: "12px auto 0",
              fontSize: 15,
              lineHeight: 1.65,
              color: bodyColor,
            }}
          >
            {children}
          </p>
        )}
        <div
          style={{
            display: "flex",
            // keep both CTAs on one row so the card holds its aspect ratio; they
            // shrink (below) rather than wrap onto a second line on narrow screens.
            flexWrap: "nowrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "clamp(6px, 1.6vw, 12px)",
            marginTop: title || children ? 24 : 0,
          }}
        >
          <a
            href={buttonHref}
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            {...(download
              ? {
                  onClick: () =>
                    (window as any).__analytics?.track("download", {
                      platform: dl.platform,
                      location: "blog_cta",
                    }),
                }
              : {})}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "clamp(5px, 1.4vw, 8px)",
              padding: "clamp(9px, 2vw, 13px) clamp(12px, 3.4vw, 34px)",
              borderRadius: 999,
              fontSize: "clamp(11px, 2.9vw, 15px)",
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: "#fff",
              backgroundColor: BLUE,
              textDecoration: "none",
              boxShadow: `0 10px 28px -10px ${BLUE}cc`,
            }}
          >
            {buttonLabel}
            {download && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path
                  d="M7 1v8.5M3 7l4 4 4-4M2 12.5h10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </a>
          {github && (
            <a
              href="https://github.com/0-AI-UG/cate"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => (window as any).__analytics?.track("github_star_badge_click", { location: "blog_cta" })}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "clamp(5px, 1.4vw, 8px)",
                padding: "clamp(9px, 2vw, 13px) clamp(11px, 2.8vw, 26px)",
                borderRadius: 999,
                fontSize: "clamp(11px, 2.9vw, 15px)",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textDecoration: "none",
                // On a dark screenshot card, a translucent light button stays legible;
                // on the plain card, fall back to a bordered surface button.
                color: bgImage ? "#fff" : "var(--text-primary)",
                backgroundColor: bgImage ? "rgba(255,255,255,0.12)" : "var(--surface-5)",
                border: `1px solid ${bgImage ? "rgba(255,255,255,0.28)" : "var(--border-strong)"}`,
                backdropFilter: bgImage ? "blur(4px)" : undefined,
              }}
            >
              <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0016 8c0-4.42-3.58-8-8-8z" />
              </svg>
              Star on GitHub
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
